import useValidationErrors, { ValidationErrors } from '../errors/useValidationErrors'
import useStandardErrors, { StandardErrors } from '../errors/useStandardErrors'
import { computed, ComputedRef, Ref, ref } from 'vue'
import { MaybeRef } from '@vueuse/core'
import qs from 'qs'
import getConfig from '../plugin/getConfig'
import useEnhancedErrors from '../errors/useEnhancedErrors'
import { EnhancedError } from '../errors/enhancers/errorEnhancerStatusCodeMap'
import { omitBy, isUndefined } from 'lodash-es'
import QueryStringAddon from 'wretch/addons/queryString'
import AbortAddon from 'wretch/addons/abort'
import wretch from 'wretch'
import { HTTP_REQUEST_TIMEOUT_MS } from 'src/shared/http/httpRequestTimeout'

export type RequestAction = 'post'
  | 'put'
  | 'get'
  | 'delete'

export interface UseRestOptions {
  include?: MaybeRef<unknown>
  fields?: MaybeRef<unknown>
}

export interface UseRestReturn<ResponseData = unknown> {
  execute: (
    action: RequestAction,
    payload?: Record<string, unknown>,
    paramQueryString?: Record<string, unknown>,
    options?: {
      withoutTenant?: boolean | undefined,
      withoutOrg?: boolean | undefined,
      withoutToken?: boolean | undefined,
    }
  ) => Promise<void>
  include: Ref<unknown>
  fields: Ref<unknown>
  standardErrors: Ref<StandardErrors<string | number>>
  enhancedError: Ref<EnhancedError | undefined>
  hasEnhancedError: ComputedRef<boolean>
  hasStandardErrors: ComputedRef<boolean>
  validationErrors: Ref<ValidationErrors>
  hasValidationErrors: ComputedRef<boolean>
  hasErrors: ComputedRef<boolean>
  isFetching: Ref<boolean>
  data: Ref<ResponseData | null>
  isFinished: Ref<boolean>
}

export default function useRest<ResponseData = unknown> (
  endpointParam: MaybeRef<string>,
  options: UseRestOptions = {},
): UseRestReturn<ResponseData> {
  const config = getConfig()
  const data = ref<ResponseData | null>(null)

  const include = ref(options.include || undefined)
  const fields = ref(options.fields || undefined)
  const endpoint = ref(endpointParam)

  const isFetching = ref(false)
  const isFinished = ref(false)

  const standardErrors = useStandardErrors()
  const validationErrors = useValidationErrors()
  const enhancedErrorsService = useEnhancedErrors()

  const hasErrors = computed(() => {
    return standardErrors.hasErrors.value ||
      validationErrors.hasErrors.value
  })

  async function execute (
    action: RequestAction,
    payload: Record<string, unknown> = {},
    queryParam: Record<string, unknown> = {},
    executeOptions: {
      withoutTenant?: boolean,
      withoutOrg?: boolean,
      withoutToken?: boolean,
    } = {},
  ) {
    standardErrors.errors.value = []
    validationErrors.errors.value = {}
    data.value = null

    isFinished.value = false

    payload.include = payload.include || include.value
    payload.fields = payload.fields || fields.value

    payload = omitBy(payload, isUndefined)
    queryParam = omitBy(queryParam, isUndefined)

    const paramQueryString = qs.stringify(queryParam)
    const url = config.apiEndpoint + '/' + endpoint.value + (paramQueryString ? ('?' + paramQueryString) : '')
    const options: RequestInit = { credentials: 'include' }

    isFetching.value = true

    try {
      const headers = config.getRequestHeaders
        ? (await config.getRequestHeaders({
            withoutOrg: executeOptions.withoutOrg,
            withoutTenant: executeOptions.withoutTenant,
            withoutToken: executeOptions.withoutToken,
          }))
        : {}

      const wretcher = wretch(url)
        .addon(QueryStringAddon)
        .addon(AbortAddon())
        .options(options)
        .headers(headers)

      const response = action === 'get'
        ? wretcher.query(payload).get().setTimeout(HTTP_REQUEST_TIMEOUT_MS)
        : payload
          ? wretcher[action](payload).setTimeout(HTTP_REQUEST_TIMEOUT_MS)
          : wretcher[action]().setTimeout(HTTP_REQUEST_TIMEOUT_MS)

      data.value = await response.json() as ResponseData
    } catch (error: any) {
      if (typeof error?.json?.errors === 'object') {
        validationErrors.errors.value = error.json.errors
      }

      if (error?.response?.data?.message) {
        standardErrors.errors.value.push({
          type: 'response message',
          message: error.response.data.message,
        })
      } else if (error?.json?.message) {
        standardErrors.errors.value.push({
          type: 'standard',
          message: error.json.message,
        })
      } else {
        standardErrors.errors.value.push({
          type: 'standard',
          message: error.message,
        })
      }

      throw error
    } finally {
      isFetching.value = false
      isFinished.value = true
    }
  }

  return {
    execute,
    include,
    fields,
    standardErrors: standardErrors.errors,
    enhancedError: enhancedErrorsService.enhancedError,
    hasEnhancedError: enhancedErrorsService.hasEnhancedError,
    hasStandardErrors: standardErrors.hasErrors,
    validationErrors: validationErrors.errors,
    hasValidationErrors: validationErrors.hasErrors,
    hasErrors,
    isFetching,
    data,
    isFinished,
  }
}

export { useRest }
